import React, {useMemo} from "react";
import { useDataWithRoute, useDebounce, useTitleFromComponent, useUrlState } from "../../../utils/hooks";
import { VsiBubble, VsiH4 } from "../../../styled/App.styled";
import { useVsiAppContext } from "../../../stores/VsiApp";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import Grid from "../../../components/Grid";
import { Card } from "reactstrap";
import {
  cloudAllComputeResources,
  cloudAllResourceGroups,
  cloudAllStorageAccounts,
  cloudAllSubscriptions
} from "../../../utils/gridColumnDefs/cloud/cloudAllUnits";
import {snakeCaseToTitleCase} from "../../../utils/Helpers";

const unitOptions = [
  {
    value: "subscriptions",
    label: "Subscriptions",
    route: 'enterprise_cloud_all_units_subscription_proc',
    defs: cloudAllSubscriptions,
  },
  {
    value: "resource-groups",
    label: "Resource Groups",
    route: 'enterprise_cloud_all_units_resource_group_proc',
    defs: cloudAllResourceGroups,
  },
  {
    value: "compute-resources",
    label: "Compute Resources",
    route: 'enterprise_cloud_all_units_compute_resource_proc',
    defs: cloudAllComputeResources,
  },
  {
    value: "storage-accounts",
    label: "Storage Accounts",
    route: 'enterprise_cloud_all_units_storage_account_proc',
    defs: cloudAllStorageAccounts,
  }
];

export default function CloudAllUnits({ context, title }) {
  useTitleFromComponent(`${context} / ${title}`);
  const { theme, client, periodId } = useVsiAppContext();
  const [params, setParams] = useUrlState({ unit: "subscriptions" });
  const { unit } = params;
  const unitOptionSelected = useMemo(() => {
    return unitOptions.find(option => option.value === unit);
  }, [unit]);

  const activeRoute = useMemo(
      () => `/api/v6/report/${unitOptionSelected.route}?client_id=${client.clientId}&period_id=${periodId || 0}`,
      [unitOptionSelected, client.clientId, periodId]
  );

  const [{recordset}, {isLoading}] = useDataWithRoute(activeRoute);


  const processedData = useMemo(() => {
    if (!recordset) return [];

    return recordset.map(row => {
      let parsedTags = {};
      try {
        if (row.tags) {
          parsedTags = JSON.parse(row.tags);
        }
      } catch (err) {
        console.error("Invalid JSON in tags:", row.tags, err);
      }
      return {
        ...row,
        ...parsedTags
      };
    });
  }, [recordset]);

  const allTagKeys = useMemo(() => {
    if (!processedData) return [];
    const defaultDefKeys = new Set(unitOptionSelected?.defs.map(col => col.field));
    const keys = new Set();
    processedData.forEach(row => {
      Object.keys(row).forEach(k => {
        if (k !== 'tags'
            && k !== '_id'
            && !defaultDefKeys.has(k)) {
          keys.add(k);
        }
      });
    });
    return Array.from(keys);
  }, [processedData, unitOptionSelected]);

  const dynamicDefs = allTagKeys.map(k => ({
    headerName: snakeCaseToTitleCase(k),
    field: k,
    hide: true,
  }));

  const finalDefs = useMemo(() => {
    return [
      ...(unitOptionSelected?.defs || []),
      ...dynamicDefs
    ];
  }, [unitOptionSelected, dynamicDefs]);

  const isLoadingDebounce = useDebounce(isLoading, 150); // to avoid flashing the grid on quick returns

  return (
    <Card className={`report-card p-2 nav-card-${theme}`}>
      <VsiH4>Cloud All Units - {unitOptionSelected.label}</VsiH4>
      <VsiBubble noAutobot>
        <VsiLabeledSelect options={unitOptions}
                          defaultValue={unitOptionSelected}
                          label={'Select Unit'}
                          minWidth={225}
                          isDisabled={isLoading}
                          isLoading={isLoading}
                          onChange={(e) => {
                            if (e.value !== unit) {
                              setParams({unit: e.value});
                            }
                          }}/>
      </VsiBubble>
      <VsiBubble noAutobot>
        <Grid data={processedData ?? []}
              isLoading={isLoadingDebounce}
              columnDefs={finalDefs || []}
        />
      </VsiBubble>
    </Card>
  );
}